import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Check, ArrowRight, BookOpen, Headphones, Loader2 } from "lucide-react";
import { API } from "@/data/landing";
import { markLeadDone } from "@/hooks/useExitIntent";

export const EbookCapture = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) { toast.error("Please enter your email."); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/leads`, { name, email });
      toast.success(res.data.message || "Success! Check your inbox.");
      markLeadDone();
      setDone(true);
      setName(""); setEmail("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="ebook" data-testid="ebook-section" className="py-24 lg:py-32">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="relative rounded-[2.5rem] bg-afwn-amber afwn-grain overflow-hidden px-8 py-14 lg:p-16">
          <div className="absolute -top-16 -right-10 w-72 h-72 rounded-full bg-white/15 blur-2xl" />
          <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7 flex flex-col sm:flex-row items-start sm:items-center gap-8">
              <div className="shrink-0 relative" data-testid="ebook-cover-wrap">
                <div className="absolute inset-0 translate-x-2 translate-y-3 rounded-md bg-afwn-navy/25 blur-md" aria-hidden />
                <img src="/ebook/cover.jpg" alt="Anxiety Relief in 7 Minutes — e-book cover" data-testid="ebook-cover" className="relative w-36 sm:w-40 lg:w-48 h-auto rounded-md shadow-2xl shadow-afwn-navy/30 ring-1 ring-white/30 -rotate-3 hover:rotate-0 transition-transform duration-500" />
              </div>
              <div>
              <span className="inline-flex items-center gap-2 px-3 py-1 text-xs tracking-wider uppercase rounded-full font-semibold bg-white/20 text-white">
                <BookOpen size={14} /> Free Gift
              </span>
              <h2 className="mt-5 font-heading font-light tracking-tight text-4xl lg:text-5xl text-white leading-[1.1]">
                Get “Anxiety Relief in 7 Minutes” — free.
              </h2>
              <p className="mt-4 text-lg text-white/90 max-w-xl">
                Instant access to the e-book <span className="font-medium">and</span> audiobook. Practical, calming tools you can use today — no cost, no strings.
              </p>
              <div className="mt-5 flex items-center gap-5 text-white/90 text-sm">
                <span className="flex items-center gap-2"><BookOpen size={16} /> E-book (PDF)</span>
                <span className="flex items-center gap-2"><Headphones size={16} /> Audiobook</span>
              </div>
              </div>
            </div>
            <div className="lg:col-span-5">
              {done ? (
                <div data-testid="ebook-success" className="bg-white rounded-2xl p-8 text-center">
                  <div className="w-14 h-14 rounded-full bg-afwn-green/10 flex items-center justify-center mx-auto text-afwn-green"><Check size={28} /></div>
                  <h3 className="mt-4 font-heading text-2xl text-afwn-navy">You’re all set!</h3>
                  <p className="mt-2 text-afwn-slate text-sm">Your free e-book + audiobook are on the way to your inbox.</p>
                </div>
              ) : (
                <form onSubmit={submit} data-testid="ebook-form" className="bg-white rounded-2xl p-6 sm:p-8 shadow-xl shadow-afwn-navy/10 space-y-3">
                  <input
                    data-testid="ebook-name-input"
                    type="text" value={name} onChange={(e) => setName(e.target.value)}
                    placeholder="Your first name (optional)"
                    className="w-full rounded-full border border-afwn-navy/15 px-5 py-3 text-afwn-navy placeholder:text-afwn-slate/60 focus:outline-none focus:ring-2 focus:ring-afwn-amber"
                  />
                  <input
                    data-testid="ebook-email-input"
                    type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                    placeholder="Your email address"
                    className="w-full rounded-full border border-afwn-navy/15 px-5 py-3 text-afwn-navy placeholder:text-afwn-slate/60 focus:outline-none focus:ring-2 focus:ring-afwn-amber"
                  />
                  <button
                    data-testid="ebook-submit-button" type="submit" disabled={loading}
                    className="w-full inline-flex items-center justify-center gap-2 rounded-full font-medium px-6 py-3.5 bg-afwn-green text-white hover:bg-afwn-greenDark transition-all duration-300 hover:scale-[1.02] disabled:opacity-70 disabled:hover:scale-100 focus:outline-none focus:ring-2 focus:ring-afwn-green focus:ring-offset-2"
                  >
                    {loading ? <><Loader2 size={18} className="animate-spin" /> Sending...</> : <>Send My Free Copy <ArrowRight size={18} /></>}
                  </button>
                  <p className="text-xs text-afwn-slate text-center pt-1">We respect your privacy. Unsubscribe anytime.</p>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
